import { useState, type FormEvent } from "react";
import Seo from "@/components/base/Seo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

const FORM_ADDR = "https://readdy.ai/api/form/da23pojk8r5te5mqsfp0";

const contactCards = [
  {
    icon: "ri-mail-line",
    label: "邮箱",
    value: "support@bibi-manga.help",
  },
  {
    icon: "ri-time-line",
    label: "服务时间",
    value: "09:00 - 21:00",
  },
  {
    icon: "ri-chat-smile-3-line",
    label: "响应",
    value: "工作日当天回复",
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "ContactPage",
  name: "联系我们 - 笔笔漫画网帮助中心",
  url: "https://manhua4.github.io/contact",
  description: "联系我们反馈笔笔漫画网使用问题，邮箱 support@bibi-manga.help，服务时间 09:00-21:00。",
  mainEntity: {
    "@type": "Organization",
    email: "support@bibi-manga.help",
    name: "笔笔漫画网帮助中心",
  },
};

type Status = "idle" | "loading" | "success" | "error";

export default function Contact() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get("company_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      window.setTimeout(() => setStatus("idle"), 3000);
      return;
    }
    fd.delete("company_alt");

    const message = String(fd.get("message") || "").trim();
    if (!message) {
      setStatus("error");
      setFormError("请填写你遇到的问题描述，方便我们为你提供帮助。");
      return;
    }

    setStatus("loading");
    setFormError("");
    try {
      const payload = new URLSearchParams();
      fd.forEach((value, key) => {
        if (value instanceof File) return;
        payload.append(key, String(value));
      });

      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.meta?.detail || parsed?.code || "";
      const spamHit =
        responseText.toLowerCase().includes("spam") ||
        (parsed?.meta && String(serverMsg).includes("spam"));

      if (res.ok && parsed?.code === "OK" && !spamHit) {
        setStatus("success");
        form.reset();
        window.setTimeout(() => setStatus("idle"), 4000);
      } else {
        setStatus("error");
        setFormError(
          (serverMsg && !String(serverMsg).includes("spam") ? serverMsg : "") ||
            "提交未成功，请稍后再试。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请检查网络后重试。");
    }
  };

  return (
    <>
      <Seo
        title="联系我们 - 笔笔漫画网帮助中心"
        description="遇到笔笔漫画网打不开、加载慢、换源失败、收藏丢失、搜索无结果、登录异常等任何问题，都可以联系我们反馈，我们会认真处理每一条留言。"
        keywords="笔笔漫画网,联系我们,问题反馈,客服,帮助中心"
        canonicalPath="/contact"
        jsonLd={jsonLd}
      />
      <Navbar />

      <main className="pt-16">
        <section className="bg-background-100 py-12 md:py-16">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="mx-auto max-w-2xl text-center">
              <span className="inline-block rounded-full bg-accent-100 px-4 py-1 text-sm font-medium text-accent-900">
                联系我们
              </span>
              <h1 className="mt-4 text-3xl font-black text-foreground-950 md:text-4xl">
                遇到问题？随时告诉我们
              </h1>
              <p className="mt-3 text-sm leading-7 text-foreground-600 md:text-base">
                关于笔笔漫画网的任何使用困扰，都可以通过下面的表单反馈，我们会认真对待每一条留言。
              </p>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[360px_1fr]">
            <aside className="space-y-4">
              {contactCards.map((c) => (
                <div
                  key={c.label}
                  className="flex items-center gap-4 rounded-2xl border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-100 text-2xl text-primary-700">
                    <i className={c.icon} />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-600">{c.label}</p>
                    <p className="mt-0.5 text-sm font-bold text-foreground-900">{c.value}</p>
                  </div>
                </div>
              ))}
              <div className="rounded-2xl border border-background-200 bg-background-50 p-5">
                <h2 className="text-sm font-bold text-foreground-900">想先自己排查？</h2>
                <p className="mt-2 text-sm leading-6 text-foreground-600">
                  大部分问题都能在首页的「六大常见问题」里找到答案，也可以直接看详细解决方案。
                </p>
                <a
                  href="/#problems"
                  className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-primary-600 hover:text-primary-700"
                >
                  <i className="ri-list-check-2" />
                  返回问题分类
                </a>
              </div>
            </aside>

            <div className="rounded-2xl border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="text-lg font-bold text-foreground-950">给我们留言</h2>
              <p className="mt-1 text-sm text-foreground-600">
                请尽量描述你遇到的问题，我们好更快定位并给出方案。
              </p>

              <form id="bibi-contact-form" data-readdy-form onSubmit={handleSubmit} className="mt-6 space-y-5">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-900">
                      你的称呼
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="怎么称呼你"
                      className="w-full rounded-lg border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition placeholder:text-foreground-600/60 focus:border-primary-300 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-900">
                      联系邮箱
                    </label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="方便我们回复你"
                      className="w-full rounded-lg border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition placeholder:text-foreground-600/60 focus:border-primary-300 focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="problem-type" className="mb-1.5 block text-sm font-medium text-foreground-900">
                    问题类型
                  </label>
                  <select
                    id="problem-type"
                    name="problem_type"
                    className="w-full rounded-lg border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition focus:border-primary-300 focus:ring-2 focus:ring-primary-200"
                  >
                    <option value="">请选择遇到的问题</option>
                    <option value="打不开">网页打不开</option>
                    <option value="加载慢">加载速度慢</option>
                    <option value="换源失败">换源失败</option>
                    <option value="收藏丢失">收藏丢失</option>
                    <option value="搜索无结果">搜索无结果</option>
                    <option value="登录异常">登录异常</option>
                    <option value="其他">其他问题</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-900">
                    问题描述 <span className="text-foreground-600">（500 字以内）</span>
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    maxLength={500}
                    placeholder="请描述你遇到的问题，比如现象、操作步骤、出现时间等"
                    className="w-full resize-none rounded-lg border border-background-200 bg-background-50 px-4 py-2.5 text-sm leading-6 text-foreground-900 outline-none transition placeholder:text-foreground-600/60 focus:border-primary-300 focus:ring-2 focus:ring-primary-200"
                  />
                </div>

                <input
                  type="text"
                  name="company_alt"
                  className="field-honey"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {status === "success" && (
                  <p className="rounded-xl bg-accent-100 px-4 py-3 text-sm text-accent-900">
                    <i className="ri-checkbox-circle-line mr-1.5" />
                    感谢你的反馈，我们已经收到，会尽快处理并回复你。
                  </p>
                )}
                {status === "error" && (
                  <p className="rounded-xl bg-secondary-100 px-4 py-3 text-sm text-secondary-900">
                    <i className="ri-error-warning-line mr-1.5" />
                    {formError}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "loading"}
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
                  {status === "loading" ? "提交中…" : "提交反馈"}
                </button>
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}